import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy, ViewChild, Output, EventEmitter, Input
} from '@angular/core';
import { Store } from '@ngrx/store';

import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

import { ReplaySubject, merge, Observable, combineLatest } from 'rxjs';
import { takeUntil, filter, map } from 'rxjs/operators';

import { AppState, Selectors, Actions as RootActions } from 'store/root-reducer';
import { CustomerActionTypes } from 'store/actions/customer.actions';
import { RecordActionTypes } from 'store/actions/record.action';

import { Customer } from 'modules/shared/models/customer.models';
import { Crp } from 'modules/shared/models/crp.models';
import { Document } from 'modules/shared/models/document.models';
import { IndividualRecord, CorporateRecord } from 'modules/shared/models/record.models';

import { CustomerDocumentsDataSource } from './customer-document-list.data-source';
import { DialogService } from 'modules/shared/components/dialog/dialog.service';

import { DocumentsService } from 'modules/core/services/documents.service';
import { SnackbarService } from 'app/services/snackbar.service';
import { Actions, ofType } from '@ngrx/effects';

@Component({
  selector: 'artemis-customer-document-list',
  templateUrl: './customer-document-list.component.html',
  styleUrls: ['./customer-document-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerDocumentListComponent implements OnInit, OnDestroy {

  @Output() editDocument: EventEmitter<Document> = new EventEmitter();
  @Input() recordId: number;

  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;

  private unsubscribe$: ReplaySubject<any> = new ReplaySubject<any>(1);

  // Names should be same as used for sorting on the backend
  readonly displayedColumns: Array<string> =
    ['showExpiryAlerts', 'file', 'doc_number', 'doc_type', 'authenticity', 'issue_date', 'expiry_date', 'updated_at', 'actions'];
  readonly pageSizeOptions: Array<number> = [5, 10, 15];

  record: IndividualRecord | CorporateRecord;
  customerDocumentsDataSource: CustomerDocumentsDataSource;

  currentCustomer$: Observable<Customer> =
    this.store.select(Selectors.customer.getCustomer).pipe(filter((res: any) => !(typeof res === 'string')));
  currentCRP$: Observable<Crp> = this.store.select(Selectors.customer.getCurrentCrp);

  loading$: Observable<boolean>;

  constructor(private store: Store<AppState>,
              private cdRef: ChangeDetectorRef,
              private action: Actions,
              private documentsService: DocumentsService,
              private dialogService: DialogService,
              private snackBar: SnackbarService) { }

  ngOnInit() {
    this.getLoadingStatus();

    merge(
      this.store.select(Selectors.record.getCorporateRecord),
      this.store.select(Selectors.record.getIndividualRecord)
    ).pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: CorporateRecord | IndividualRecord) => {
        if (res) {
          this.record = res;
          this.customerDocumentsDataSource = new CustomerDocumentsDataSource(this.store, res.id);
          this.customerDocumentsDataSource.sort = this.sort;
          this.customerDocumentsDataSource.paginator = this.paginator;
          this.customerDocumentsDataSource.loadDocuments();
          this.editDocument.next(undefined);
          this.cdRef.detectChanges();
        }
      });

      this.action.pipe(
        ofType(RootActions.customer.CustomerActionTypes.RELOAD_CRP_LIST),
        takeUntil(this.unsubscribe$)
      ).subscribe(() => {
        this.customerDocumentsDataSource.loadDocuments();
      });
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  /**
   * Get loading status from store
   * @method getLoadingStatus
   */
  private getLoadingStatus(): void {
    this.loading$ = combineLatest([
      this.store.select(Selectors.record.getLoading),
      this.store.select(Selectors.customer.getLoading)
    ]).pipe(
      takeUntil(this.unsubscribe$),
      map(([recordLoading, customerLoading]: [Array<RecordActionTypes>, Array<CustomerActionTypes>]) => {
        return recordLoading.includes(RecordActionTypes.GET_INDIVIDUAL_RECORD) ||
               recordLoading.includes(RecordActionTypes.GET_CORPORATE_RECORD) ||
               customerLoading.includes(CustomerActionTypes.GET_CUSTOMER_DOCUMENTS);
      })
    );
  }

  /**
   * Edit customer document
   * @method editDocumentClick
   */
  editDocumentClick(document: Document): void {
    this.editDocument.next(document);
  }

  /**
   * Add a new document
   * @method addDocument
   */
  addDocument(): void {
    this.editDocument.next(null);
  }

  deleteDocument(documentId: number) {
    this.dialogService.confirm('Are you sure you want to delete?', 'NOTICE').subscribe(
      res => {
        if (res === 'submit') {
          this.store.dispatch(new RootActions.document.DeleteDocument(documentId));
        }
      }
    );
  }

  /**
   * Checks if document is expiring/expired
   * @method checkDocumentExpiring
   */
  checkDocumentExpiring(customerDocument: Document): boolean {
    if (customerDocument.expiryDate && customerDocument.showExpiryAlerts) {
      return (new Date().getTime()) >= (new Date(customerDocument.expiryDate).getTime()) ||
        (new Date(customerDocument.expiryDate).getTime() - 60 * 60 * 24 * 90 * 1000) <= (new Date().getTime());
    } else {
      return false;
    }
  }

  /**
   * Toggle notification about expiring document
   * @method toggleNotification
   */
  toggleNotification(expiringDocumentId: number, $event: MatSlideToggleChange): void {
    this.documentsService
      .toggleNotification(expiringDocumentId, $event.checked)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (documentsRes: Document) => {
          this.snackBar.success('Expiring document notification dismissed');
          this.customerDocumentsDataSource.loadDocuments();
        },
        (error: any) => {
          this.snackBar.error('Error: Failed to dismiss expiry document notification');
          this.cdRef.detectChanges();
        }
      );
  }
}
